import qs from 'qs';
import { CrossDomainStorage } from '@packages/shared/core/common/services';
import { Logger } from '@packages/shared/core/common/logger/interfaces';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { UtmTags } from '@packages/shared/core/user/entities';
import { POSSIBLE_UTM_TAGS } from '../../constants';
import { UtmTagsService } from './UtmTagsService';

export class UtmTagsClientService implements UtmTagsService {
  private readonly crossDomainStorage: CrossDomainStorage;

  private readonly logger: Logger;

  private queryUtmTags: UtmTags = {};

  private constructor(crossDomainStorage: CrossDomainStorage, logger: Logger) {
    this.crossDomainStorage = crossDomainStorage;
    this.logger = logger;
  }

  getUtmSource() {
    return this.getUtmTags().utm_source;
  }

  getUtmTags() {
    return this.getTagsFromCache();
  }

  getQueryUtmTags() {
    return this.queryUtmTags;
  }

  static createFromQueryParams(crossDomainStorage: CrossDomainStorage, logger: Logger) {
    const utmService = new UtmTagsClientService(crossDomainStorage, logger);
    utmService.loadFormQueryParams();

    return utmService;
  }

  updateUtmTags(utmTags: UtmTags) {
    const oldUtmTags = this.getUtmTags();
    const newUtmTags = { ...oldUtmTags, ...utmTags };

    this.saveToCache(newUtmTags);

    return this.getUtmTags();
  }

  private getUtmTagsFromQueryParams() {
    const newUtmTags: Record<string, string> = {};

    const parsedQs: UtmTags = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    POSSIBLE_UTM_TAGS.forEach((utmTag) => {
      if (utmTag in parsedQs) {
        newUtmTags[utmTag] = this.extractUtmTagFromParsedQs(parsedQs[utmTag]);
      }
    });

    return newUtmTags;
  }

  private loadFormQueryParams() {
    const newUtmTags = this.getUtmTagsFromQueryParams();

    this.queryUtmTags = newUtmTags;
    this.updateUtmTags(newUtmTags);
  }

  private saveToCache(utmTags: UtmTags) {
    try {
      this.crossDomainStorage.saveUtmTags(utmTags);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
    }
  }

  private getTagsFromCache(): UtmTags {
    try {
      return this.crossDomainStorage.getUtmTags();
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
      return {};
    }
  }

  private extractUtmTagFromParsedQs(parsedQs: undefined | string | Array<string>): string {
    if (!parsedQs) return '';

    return Array.isArray(parsedQs) ? parsedQs[0] : parsedQs;
  }
}
